/**
 * PujcovnaSpravaPage — admin tools of the rental module (`/pujcovna/sprava?nastroj=…`): the pobočky and
 * the tarify (each: list, create on a full page, edit in a modal, delete), the firma's settings (the turnaround
 * gap) and the module's change history (shared HistorieWorkspace over `historie_pujcovna`). Gated on `pujcovna:admin` like the API;
 * the page waits for membership to resolve so it never queries what it can't read.
 */

import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Badge } from '@/components/Badge';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Crown } from '@/components/Crown';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useModuleAccess } from '@/lib/permissions';
import { HistorieWorkspace } from '@/modules/historie/HistorieWorkspace';
import {
  useDeletePobockaMutation,
  useDeletePojistovnaMutation,
  useDeleteTarifMutation,
  usePobockyQuery,
  usePojistovnyQuery,
  useTarifyQuery,
  useVyjimkyPocetQuery,
  type PujcovnaPobocka,
  type PujcovnaPojistovna,
  type PujcovnaTarif,
} from './api';
import type { PujcovnaSpravaSearch, SpravaNastroj } from './helpers';
import { hodinyShrnuti } from './otviraci-doba-helpers';
import { PobockaEditModal } from './PobockaEditModal';
import { PojistovnaEditModal } from './PojistovnaEditModal';
import { PujcovnaBranaTool } from './PujcovnaBranaTool';
import { PujcovnaNastaveniTool } from './PujcovnaNastaveniTool';
import { PujcovnaVyjimkyTool } from './PujcovnaVyjimkyTool';
import { cenikShrnuti } from './cenik-helpers';
import { TarifEditModal } from './TarifEditModal';

const NASTROJE: Array<{ value: SpravaNastroj; label: string; count?: number }> = [
  { value: 'pobocky', label: 'Pobočky' },
  { value: 'tarify', label: 'Tarify' },
  { value: 'pojistovny', label: 'Pojišťovny' },
  { value: 'brana', label: 'Platební brána' },
  { value: 'vyjimky', label: 'Platby k řešení' },
  { value: 'nastaveni', label: 'Nastavení' },
  { value: 'historie', label: 'Historie změn' },
];

function PobockyTool() {
  const pobocky = usePobockyQuery();
  const del = useDeletePobockaMutation();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<PujcovnaPobocka | null>(null);

  const remove = async (p: PujcovnaPobocka) => {
    const ok = await confirm({
      title: (
        <>
          Smazat pobočku <em>{p.nazev}</em>?
        </>
      ),
      description: 'Pobočku nelze smazat, dokud jsou na ní vozidla.',
      tone: 'danger',
      confirmLabel: 'Smazat',
    });
    if (!ok) return;
    del.mutate(p.id, {
      onSuccess: () => toast.ok('Pobočka smazána.'),
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se smazat pobočku.'}`),
    });
  };

  const columns: DataTableColumn<PujcovnaPobocka>[] = [
    {
      id: 'nazev',
      header: 'Pobočka',
      width: 'minmax(160px, 1fr)',
      priority: 'primary',
      cell: (p) => <span className="text-[14px] text-ink">{p.nazev}</span>,
    },
    {
      id: 'adresa',
      header: 'Adresa',
      width: 'minmax(180px, 1.4fr)',
      priority: 'secondary',
      cell: (p) => <span className="text-[13px] text-ink-2">{p.adresa || '—'}</span>,
    },
    {
      id: 'kontakt',
      header: 'Kontakt',
      width: 'minmax(180px, 1.2fr)',
      priority: 'secondary',
      cell: (p) => (
        <span className="font-mono text-[12px] text-ink-3">
          {[p.telefon, p.email].filter(Boolean).join(' · ') || '—'}
        </span>
      ),
    },
    {
      id: 'web',
      header: 'Na webu',
      width: 'minmax(200px, 1.2fr)',
      priority: 'secondary',
      cell: (p) =>
        p.slug && p.otviraci_doba.length > 0 ? (
          <span className="flex flex-col text-[12.5px] text-ink-2">
            <span className="font-mono text-[12px] text-ink-3">{p.slug}</span>
            {hodinyShrnuti(p.otviraci_doba)}
          </span>
        ) : (
          <Badge tone="warn" size="sm">
            {p.slug ? 'Bez otevírací doby' : 'Bez odkazu'}
          </Badge>
        ),
    },
    {
      id: 'akce',
      header: '',
      width: '180px',
      align: 'right',
      priority: 'meta',
      cell: (p) => (
        <span className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditing(p)}>
            Upravit
          </Button>
          <Button variant="secondary" size="sm" onClick={() => remove(p)} disabled={del.isPending}>
            Smazat
          </Button>
        </span>
      ),
    },
  ];

  const rows = pobocky.data ?? [];

  // Same shape as Platforma → Firmy: the body is the gray surface with a bordered table panel on it.
  return (
    <div data-testid="pobocky-list" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
      {pobocky.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : pobocky.isError ? (
        <EmptyState eyebrow="Chyba" title="Pobočky se nepodařilo načíst" />
      ) : rows.length === 0 ? (
        <EmptyState
          eyebrow="Bez poboček"
          title="Zatím tu není žádná pobočka"
          description="Založte první pobočku tlačítkem „Nová pobočka“ vpravo nahoře. Vozidla půjčovny se později řadí pod pobočku."
        />
      ) : (
        <DataTable<PujcovnaPobocka>
          aria-label="Pobočky půjčovny"
          variant="bordered"
          columns={columns}
          data={rows}
          getRowId={(p) => String(p.id)}
        />
      )}

      {editing && (
        <PobockaEditModal pobocka={editing} open onOpenChange={(open) => !open && setEditing(null)} />
      )}
      {dialog}
    </div>
  );
}

/** What an insurer pays, in one line: the tarify with a price, and the threshold. */
const sazbyShrnuti = (p: PujcovnaPojistovna): string => {
  const s = p.sazby.filter((x) => x.cena1 != null || x.cena2 != null);
  if (s.length === 0) return '';
  const cena = (n: number | null) => (n == null ? 'nehradí' : `${new Intl.NumberFormat('cs-CZ').format(n)} Kč`);
  return `${s.length} z ${p.sazby.length} tarifů` + (p.prah_dni > 0 ? ` · druhá cena od ${p.prah_dni}. dne` : ' · jedna cena') + (s.length === 1 ? ` · ${cena(s[0]!.cena1)} / ${cena(s[0]!.cena2)}` : '');
};

function PojistovnyTool() {
  const pojistovny = usePojistovnyQuery();
  const del = useDeletePojistovnaMutation();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<PujcovnaPojistovna | null>(null);

  const remove = async (p: PujcovnaPojistovna) => {
    const ok = await confirm({
      title: (
        <>
          Smazat pojišťovnu <em>{p.nazev}</em>?
        </>
      ),
      description: 'Pojišťovnu nelze smazat, dokud jsou na ni vedené rezervace. Smažou se i její sazby.',
      tone: 'danger',
      confirmLabel: 'Smazat',
    });
    if (!ok) return;
    del.mutate(p.id, {
      onSuccess: () => toast.ok('Pojišťovna smazána.'),
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se smazat pojišťovnu.'}`),
    });
  };

  const columns: DataTableColumn<PujcovnaPojistovna>[] = [
    { id: 'nazev', header: 'Pojišťovna', width: 'minmax(200px, 1fr)', priority: 'primary', cell: (p) => <span className="text-[14px] text-ink">{p.nazev}</span> },
    {
      id: 'sazby',
      header: 'Sazby',
      width: 'minmax(240px, 1.6fr)',
      priority: 'secondary',
      cell: (p) => {
        const s = sazbyShrnuti(p);
        return s ? <span className="text-[13px] text-ink">{s}</span> : <Badge tone="warn" size="sm">Bez sazeb</Badge>;
      },
    },
    {
      id: 'akce',
      header: '',
      width: '180px',
      align: 'right',
      priority: 'meta',
      cell: (p) => (
        <span className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditing(p)}>
            Upravit
          </Button>
          <Button variant="secondary" size="sm" onClick={() => remove(p)} disabled={del.isPending}>
            Smazat
          </Button>
        </span>
      ),
    },
  ];

  const rows = pojistovny.data ?? [];

  return (
    <div data-testid="pojistovny-list" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
      {pojistovny.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : pojistovny.isError ? (
        <EmptyState eyebrow="Chyba" title="Pojišťovny se nepodařilo načíst" />
      ) : rows.length === 0 ? (
        <EmptyState
          eyebrow="Bez pojišťoven"
          title="Zatím tu není žádná pojišťovna"
          description="Založte první pojišťovnu tlačítkem „Nová pojišťovna“ vpravo nahoře. Pojišťovna platí denní cenu za náhradní vozidlo podle tarifu vozu."
        />
      ) : (
        <DataTable<PujcovnaPojistovna> aria-label="Pojišťovny půjčovny" variant="bordered" columns={columns} data={rows} getRowId={(p) => String(p.id)} />
      )}

      {editing && <PojistovnaEditModal pojistovna={editing} open onOpenChange={(open) => !open && setEditing(null)} />}
      {dialog}
    </div>
  );
}

function TarifyTool() {
  const tarify = useTarifyQuery();
  const del = useDeleteTarifMutation();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<PujcovnaTarif | null>(null);

  const remove = async (t: PujcovnaTarif) => {
    const ok = await confirm({
      title: (
        <>
          Smazat tarif <em>{t.nazev}</em>?
        </>
      ),
      description: 'Tarif nelze smazat, dokud ho používají vozidla.',
      tone: 'danger',
      confirmLabel: 'Smazat',
    });
    if (!ok) return;
    del.mutate(t.id, {
      onSuccess: () => toast.ok('Tarif smazán.'),
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se smazat tarif.'}`),
    });
  };

  const columns: DataTableColumn<PujcovnaTarif>[] = [
    {
      id: 'nazev',
      header: 'Tarif',
      width: 'minmax(180px, 1fr)',
      priority: 'primary',
      cell: (t) => <span className="text-[14px] text-ink">{t.nazev}</span>,
    },
    {
      id: 'cenik',
      header: 'Ceník',
      width: 'minmax(200px, 1.2fr)',
      priority: 'secondary',
      cell: (t) => {
        const shrnuti = cenikShrnuti(t);
        return shrnuti ? <span className="text-[13px] text-ink">{shrnuti}</span> : <Badge tone="warn" size="sm">Bez ceníku</Badge>;
      },
    },
    {
      id: 'vop',
      header: 'Odkaz na VOP',
      width: 'minmax(220px, 1.6fr)',
      priority: 'secondary',
      cell: (t) =>
        t.vop_url ? (
          <a
            href={t.vop_url}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate font-mono text-[12px] text-accent-text hover:underline"
          >
            {t.vop_url}
          </a>
        ) : (
          <span className="text-[13px] text-ink-3">—</span>
        ),
    },
    {
      id: 'akce',
      header: '',
      width: '180px',
      align: 'right',
      priority: 'meta',
      cell: (t) => (
        <span className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditing(t)}>
            Upravit
          </Button>
          <Button variant="secondary" size="sm" onClick={() => remove(t)} disabled={del.isPending}>
            Smazat
          </Button>
        </span>
      ),
    },
  ];

  const rows = tarify.data ?? [];

  return (
    <div data-testid="tarify-list" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
      {tarify.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : tarify.isError ? (
        <EmptyState eyebrow="Chyba" title="Tarify se nepodařilo načíst" />
      ) : rows.length === 0 ? (
        <EmptyState
          eyebrow="Bez tarifů"
          title="Zatím tu není žádný tarif"
          description="Založte první tarif tlačítkem „Nový tarif“ vpravo nahoře. Tarif určuje cenu vozidel: marži, pásma délky pronájmu a sezóny."
        />
      ) : (
        <DataTable<PujcovnaTarif>
          aria-label="Tarify půjčovny"
          variant="bordered"
          columns={columns}
          data={rows}
          getRowId={(t) => String(t.id)}
        />
      )}

      {editing && <TarifEditModal tarif={editing} open onOpenChange={(open) => !open && setEditing(null)} />}
      {dialog}
    </div>
  );
}

export function PujcovnaSpravaPage() {
  const navigate = useNavigate();
  const { can, resolved } = useModuleAccess();
  // The tool lives in the URL so a create page can return to the tool it came from.
  const search = useSearch({ from: '/pujcovna/sprava' }) as PujcovnaSpravaSearch;
  const tool: SpravaNastroj = search.nastroj ?? 'pobocky';
  const setTool = (next: SpravaNastroj) =>
    navigate({ to: '/pujcovna/sprava', search: next === 'pobocky' ? {} : { nastroj: next }, replace: true });
  // Same query as the tool below (one cache entry): only the count in the band.
  const pobocky = usePobockyQuery();
  const tarify = useTarifyQuery(resolved && can('pujcovna', 'admin'));
  const pojistovny = usePojistovnyQuery(resolved && can('pujcovna', 'admin'));
  // The queue's open count rides on its tab (a one-row read, not the page of items the tool loads).
  const otevreneVyjimky = useVyjimkyPocetQuery(resolved && can('pujcovna', 'admin')).data;
  const nastroje = NASTROJE.map((n) => (n.value === 'vyjimky' && otevreneVyjimky ? { ...n, count: otevreneVyjimky } : n));

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <Crown>
        <Breadcrumb
          segments={[
            { label: 'Půjčovna', href: '/pujcovna' },
            { label: 'Správa' },
            { label: NASTROJE.find((n) => n.value === tool)?.label ?? '' },
          ]}
        />
        <Crown.Row className="mt-3">
          <DisplayHeading>Správa</DisplayHeading>
        </Crown.Row>
        {/* the tools are the page's tab strip, as on the other Správa pages with several tools (vozidla) */}
        {resolved && can('pujcovna', 'admin') && (
          <Subtabs<SpravaNastroj> aria-label="Správa půjčovny — nástroje" value={tool} onChange={setTool} items={nastroje} />
        )}
      </Crown>

      {!resolved ? (
        <div className="flex flex-col gap-2 px-8 pt-5">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : !can('pujcovna', 'admin') ? (
        <div data-testid="pujcovna-sprava-denied" className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5">
          <EmptyState
            eyebrow="403"
            title="Správa půjčovny vyžaduje administrátorský přístup k modulu."
            description="Pro přístup požádejte správce firmy."
          />
        </div>
      ) : (
        <>
          {/* One band like every other list page (paper bg, hairline, gutter): the count and the page's
              single primary action on the right; a tool with neither (Nastavení, Historie) has no band. */}
          {(tool === 'pobocky' || tool === 'tarify' || tool === 'pojistovny') && (
            <Toolband>
              <Toolband.Spacer />
              {tool === 'tarify' && (
                <>
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                    {tarify.isLoading ? '—' : `${tarify.data?.length ?? 0} tarifů`}
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Icon name="plus" size={12} />}
                    onClick={() => navigate({ to: '/pujcovna/sprava/tarify/novy' })}
                  >
                    Nový tarif
                  </Button>
                </>
              )}
              {tool === 'pojistovny' && (
                <>
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                    {pojistovny.isLoading ? '—' : `${pojistovny.data?.length ?? 0} pojišťoven`}
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Icon name="plus" size={12} />}
                    onClick={() => navigate({ to: '/pujcovna/sprava/pojistovny/nova' })}
                  >
                    Nová pojišťovna
                  </Button>
                </>
              )}
              {tool === 'pobocky' && (
                <>
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                    {pobocky.isLoading ? '—' : `${pobocky.data?.length ?? 0} poboček`}
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Icon name="plus" size={12} />}
                    onClick={() => navigate({ to: '/pujcovna/sprava/pobocky/nova' })}
                  >
                    Nová pobočka
                  </Button>
                </>
              )}
            </Toolband>
          )}
          {tool === 'historie' ? (
            <HistorieWorkspace moduly={['pujcovna']} />
          ) : tool === 'nastaveni' ? (
            <PujcovnaNastaveniTool />
          ) : tool === 'brana' ? (
            <PujcovnaBranaTool />
          ) : tool === 'vyjimky' ? (
            <PujcovnaVyjimkyTool />
          ) : tool === 'tarify' ? (
            <TarifyTool />
          ) : tool === 'pojistovny' ? (
            <PojistovnyTool />
          ) : (
            <PobockyTool />
          )}
        </>
      )}
    </Shell>
  );
}
